import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Leaf, LogOut, Mail, Phone, TrendingUp, Users, FileText, Send,
  DollarSign, Loader2, CircleCheck, Search, Filter, X, ArrowRight,
  Trash2, UserPlus, Plus, StickyNote, Save, Pencil
} from "lucide-react";
import { api, getToken, clearToken } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import RemindersSection from "./RemindersSection";

const STATUS_OPTIONS = [
  { value: "ny", label: "Ny", color: "bg-blue-100 text-blue-800" },
  { value: "kontaktet", label: "Kontaktet", color: "bg-amber-100 text-amber-800" },
  { value: "booket", label: "Booket", color: "bg-purple-100 text-purple-800" },
  { value: "udført", label: "Udført", color: "bg-emerald-100 text-emerald-800" },
  { value: "afvist", label: "Afvist", color: "bg-gray-200 text-gray-700" },
];

const StatusBadge = ({ status }) => {
  const s = STATUS_OPTIONS.find((x) => x.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${s.color}`}>
      {s.label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDKK = (n) => new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(n || 0);

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupTo, setFollowupTo] = useState(null);
  const [followupForm, setFollowupForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [authed] = useState(() => !!getToken());
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [addCustForm, setAddCustForm] = useState({
    name: "", email: "", phone: "", address: "",
    length_meters: 20, height_category: "1,5 – 2,5 meter",
    include_waste_removal: true, estimated_price: 640,
    notes: "", status: "ny",
  });
  const [addingCust, setAddingCust] = useState(false);
  const [extraForm, setExtraForm] = useState({ leadId: null, label: "", amount: "" });
  const [addingExtra, setAddingExtra] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/admin/stats"), api.get("/admin/customers")]);
      setStats(s.data);
      setCustomers(c.data);
      // Preserve selected customer reference across refresh
      if (selected) {
        const still = c.data.find((cu) => cu.email === selected.email);
        setSelected(still || null);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        clearToken();
        window.location.href = "/admin/login";
      } else {
        toast.error("Kunne ikke hente data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  useEffect(() => {
    setNoteDraft(selected?.note || "");
  }, [selected?.email]);

  if (!authed) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    clearToken();
    window.location.href = "/admin/login";
  };

  const updateStatus = async (lead, newStatus) => {
    try {
      await api.patch(`/admin/leads/${lead.type}/${lead.id}`, { status: newStatus });
      toast.success("Status opdateret");
      lead.status = newStatus;
      setCustomers([...customers]);
      if (selected) setSelected({ ...selected });
    } catch {
      toast.error("Kunne ikke opdatere status");
    }
  };

  const openFollowup = (customer, lead) => {
    setFollowupTo({ customer, lead });
    const service = lead?.service || "vores service";
    const defaultSubject = `Er det tid til ${lead?.type === "quote" ? "hækklipning" : "vedligehold"} igen?`;
    const defaultMsg = `Vi har tidligere lavet ${service.toLowerCase()} for dig${lead?.price ? ` (${formatDKK(lead.price)})` : ""}. Skal vi kigge forbi igen i år?

Hvis du er interesseret, kan du blot svare på denne mail — så vender vi tilbage med en tid.

Rigtig god dag!`;
    setFollowupForm({ subject: defaultSubject, message: defaultMsg });
    setFollowupOpen(true);
  };

  const sendFollowup = async (e) => {
    e.preventDefault();
    if (!followupForm.subject || !followupForm.message) return;
    setSending(true);
    try {
      await api.post("/admin/followup", {
        to_email: followupTo.customer.email,
        to_name: followupTo.customer.name,
        subject: followupForm.subject,
        message: followupForm.message,
        lead_id: followupTo.lead?.id,
        lead_type: followupTo.lead?.type,
      });
      toast.success(`Follow-up sendt til ${followupTo.customer.email}`);
      setFollowupOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende e-mail");
    } finally {
      setSending(false);
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Slet dette ${lead.type === "quote" ? "tilbud" : "kontakt"}? Handlingen kan ikke fortrydes.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.type}/${lead.id}`);
      toast.success("Lead slettet");
      await load();
      setSelected(null);
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const deleteCustomer = async (customer) => {
    if (!window.confirm(`Slet KUNDEN "${customer.name || customer.email}" og alle deres ${customer.lead_count} lead(s)? Handlingen kan ikke fortrydes.`)) return;
    try {
      await api.delete(`/admin/customers/${encodeURIComponent(customer.email)}`);
      toast.success("Kunde slettet");
      setSelected(null);
      await load();
    } catch {
      toast.error("Kunne ikke slette kunden");
    }
  };

  const createCustomer = async (e) => {
    e.preventDefault();
    setAddingCust(true);
    try {
      await api.post("/admin/quotes", {
        ...addCustForm,
        length_meters: Number(addCustForm.length_meters),
        estimated_price: Number(addCustForm.estimated_price),
      });
      toast.success("Kunde tilføjet");
      setAddCustOpen(false);
      setAddCustForm({
        name: "", email: "", phone: "", address: "",
        length_meters: 20, height_category: "1,5 – 2,5 meter",
        include_waste_removal: true, estimated_price: 640,
        notes: "", status: "ny",
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oprette");
    } finally {
      setAddingCust(false);
    }
  };

  const addExtra = async (lead) => {
    if (!extraForm.label || !extraForm.amount) {
      toast.error("Udfyld både beskrivelse og beløb");
      return;
    }
    setAddingExtra(true);
    try {
      await api.post(`/admin/leads/quote/${lead.id}/extras`, {
        label: extraForm.label,
        amount: Number(extraForm.amount),
      });
      toast.success("Tillæg tilføjet");
      setExtraForm({ leadId: null, label: "", amount: "" });
      await load();
    } catch {
      toast.error("Kunne ikke tilføje tillæg");
    } finally {
      setAddingExtra(false);
    }
  };

  const removeExtra = async (lead, extraId) => {
    try {
      await api.delete(`/admin/leads/quote/${lead.id}/extras/${extraId}`);
      toast.success("Tillæg fjernet");
      await load();
    } catch {
      toast.error("Kunne ikke fjerne");
    }
  };

  const saveNote = async () => {
    if (!selected) return;
    setSavingNote(true);
    try {
      await api.put(`/admin/customers/${encodeURIComponent(selected.email)}/notes`, { note: noteDraft });
      toast.success("Note gemt");
      await load();
    } catch {
      toast.error("Kunne ikke gemme noten");
    } finally {
      setSavingNote(false);
    }
  };

  const openEdit = (lead) => {
    if (lead.type === "quote") {
      setEditForm({
        name: selected?.name || "",
        email: selected?.email || "",
        phone: selected?.phone || "",
        address: lead.address || "",
        length_meters: lead.length_meters || 0,
        height_category: lead.height_category || "1,5 – 2,5 meter",
        include_waste_removal: lead.include_waste_removal ?? true,
        estimated_price: lead.price || 0,
        notes: lead.message || "",
        status: lead.status,
      });
    } else {
      setEditForm({
        name: selected?.name || "",
        email: selected?.email || "",
        phone: selected?.phone || "",
        service: lead.service || "",
        message: lead.message || "",
        status: lead.status,
      });
    }
    setEditLead(lead);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editLead) return;
    setSavingEdit(true);
    try {
      const payload = { ...editForm };
      if (payload.length_meters !== undefined) payload.length_meters = Number(payload.length_meters);
      if (payload.estimated_price !== undefined) payload.estimated_price = Number(payload.estimated_price);
      await api.put(`/admin/leads/${editLead.type}/${editLead.id}`, payload);
      toast.success("Tilbud opdateret");
      setEditLead(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setSavingEdit(false);
    }
  };

  const openQuoteFromContact = (customer, contactLead) => {
    setAddCustForm({
      name: customer?.name || "",
      email: customer?.email || "",
      phone: customer?.phone || "",
      address: "",
      length_meters: 20,
      height_category: "1,5 – 2,5 meter",
      include_waste_removal: true,
      estimated_price: 640,
      notes: contactLead?.message ? `Fra kundens besked: ${contactLead.message}` : "",
      status: "ny",
    });
    setAddCustOpen(true);
  };

  const filtered = customers.filter((c) => {
    if (search) {
      const s = search.toLowerCase();
      if (!c.email.toLowerCase().includes(s) && !(c.name || "").toLowerCase().includes(s) && !(c.phone || "").includes(search)) {
        return false;
      }
    }
    if (filter !== "all") {
      const hasStatus = c.leads.some((l) => l.status === filter);
      if (!hasStatus) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      {/* Admin nav */}
      <header className="border-b border-brand-border bg-brand-surface sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-primary text-brand-bg">
              <Leaf className="h-4.5 w-4.5" strokeWidth={1.8} />
            </span>
            <div>
              <div className="font-display text-base font-medium leading-tight">Syddansk Group</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">Admin Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-brand-muted hover:text-brand-primary" data-testid="admin-view-site">
              Se hjemmesiden
            </Link>
            <button
              onClick={() => setAddCustOpen(true)}
              data-testid="admin-add-customer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-4 py-2 text-sm font-medium hover:bg-brand-hover"
            >
              <UserPlus className="h-4 w-4" /> Tilføj kunde
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-brand-border px-4 py-2 text-sm hover:bg-brand-soft"
            >
              <LogOut className="h-4 w-4" /> Log ud
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 space-y-10">
        {/* Stats */}
        <section>
          <h1 className="font-display text-4xl tracking-tight text-brand-text mb-8">Oversigt</h1>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Users} label="Kunder" value={stats?.unique_customers ?? 0} data-testid="stat-customers" />
            <StatCard icon={FileText} label="Total leads" value={stats?.total_leads ?? 0} sub={`${stats?.total_quotes ?? 0} tilbud · ${stats?.total_contacts ?? 0} beskeder`} data-testid="stat-leads" />
            <StatCard icon={DollarSign} label="Estimeret værdi" value={formatDKK(stats?.total_quote_value)} sub={`Snit: ${formatDKK(stats?.avg_quote_value)}`} data-testid="stat-value" />
            <StatCard icon={TrendingUp} label="Konvertering" value={`${stats?.conversion_rate ?? 0}%`} sub={`${stats?.followups_sent ?? 0} follow-ups sendt`} data-testid="stat-conversion" />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-1 bg-brand-surface rounded-3xl border border-brand-border p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-4">Status-fordeling</div>
              <div className="space-y-3">
                {STATUS_OPTIONS.map((s) => (
                  <div key={s.value} className="flex items-center justify-between">
                    <StatusBadge status={s.value} />
                    <span className="font-medium text-brand-text">{stats?.status_counts?.[s.value] ?? 0}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 bg-brand-surface rounded-3xl border border-brand-border p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-4">Leads sidste 6 måneder</div>
              <MonthlyChart labels={stats?.monthly_labels || []} values={stats?.monthly_values || []} />
            </div>
          </div>
        </section>

        {/* Customers */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="font-display text-3xl tracking-tight text-brand-text">Kunder</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                <Input
                  data-testid="admin-search"
                  placeholder="Søg navn, e-mail, telefon..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10 w-64 bg-brand-surface"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="admin-filter" className="w-40 h-10 bg-brand-surface">
                  <Filter className="h-4 w-4 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-brand-border p-16 text-center text-brand-muted">
              Ingen kunder matcher dine filtre endnu.
            </div>
          ) : (
            <div className="rounded-3xl border border-brand-border bg-brand-surface overflow-hidden">
              <table className="w-full">
                <thead className="bg-brand-soft/60 border-b border-brand-border">
                  <tr>
                    <th className="text-left py-3 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium">Kunde</th>
                    <th className="text-left py-3 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium hidden md:table-cell">Kontakt</th>
                    <th className="text-left py-3 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium">Leads</th>
                    <th className="text-left py-3 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium hidden lg:table-cell">Værdi</th>
                    <th className="text-left py-3 px-6 text-xs uppercase tracking-[0.18em] text-brand-muted font-medium hidden lg:table-cell">Sidst</th>
                    <th className="py-3 px-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => (
                    <tr
                      key={c.email}
                      data-testid={`admin-customer-row-${i}`}
                      className="border-b border-brand-border/60 last:border-0 hover:bg-brand-soft/30 cursor-pointer"
                      onClick={() => setSelected(c)}
                    >
                      <td className="py-4 px-6">
                        <div className="font-medium text-brand-text">{c.name || c.email}</div>
                        <div className="text-xs text-brand-muted md:hidden">{c.email}</div>
                      </td>
                      <td className="py-4 px-6 hidden md:table-cell">
                        <div className="text-sm text-brand-text">{c.email}</div>
                        {c.phone && <div className="text-xs text-brand-muted mt-0.5">{c.phone}</div>}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{c.lead_count}</span>
                          <div className="flex flex-wrap gap-1">
                            {[...new Set(c.leads.map((l) => l.status))].slice(0, 2).map((st) => (
                              <StatusBadge key={st} status={st} />
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 hidden lg:table-cell text-brand-text font-medium">
                        {c.total_value ? formatDKK(c.total_value) : "—"}
                      </td>
                      <td className="py-4 px-6 hidden lg:table-cell text-sm text-brand-muted">
                        {formatDate(c.last_contact)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <ArrowRight className="h-4 w-4 text-brand-muted inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <RemindersSection />
      </main>

      {/* Customer detail drawer */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl bg-brand-bg max-h-[90vh] overflow-y-auto" data-testid="admin-customer-drawer">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-brand-text">
                  {selected.name || selected.email}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm">
                    <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1.5 text-brand-primary hover:underline">
                      <Mail className="h-3.5 w-3.5" /> {selected.email}
                    </a>
                    {selected.phone && (
                      <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-1.5 text-brand-primary hover:underline">
                        <Phone className="h-3.5 w-3.5" /> {selected.phone}
                      </a>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-2 mt-2 items-center flex-wrap">
                <button
                  onClick={() => openFollowup(selected, selected.leads[0])}
                  data-testid="admin-followup-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-4 py-2 text-sm font-medium hover:bg-brand-hover"
                >
                  <Send className="h-4 w-4" /> Send follow-up
                </button>
                <button
                  onClick={() => deleteCustomer(selected)}
                  data-testid="admin-delete-customer"
                  className="inline-flex items-center gap-2 rounded-full border border-red-200 text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Slet kunde
                </button>
                {selected.total_value > 0 && (
                  <div className="ml-auto text-right">
                    <div className="text-xs text-brand-muted">Total værdi</div>
                    <div className="font-display text-2xl text-brand-primary">{formatDKK(selected.total_value)}</div>
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-2xl border border-brand-border bg-brand-surface p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-muted">
                    <StickyNote className="h-3.5 w-3.5 text-brand-primary" /> Private noter
                  </div>
                  <button
                    type="button"
                    onClick={saveNote}
                    disabled={savingNote || noteDraft === (selected.note || "")}
                    data-testid="admin-save-note"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary text-brand-bg px-3 py-1.5 text-xs font-medium hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Gem note
                  </button>
                </div>
                <Textarea
                  data-testid="admin-customer-note"
                  rows={3}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Fx: 'Vil helst have besøg om morgenen', 'Hunden er venlig', 'Kunden er tilbageholdende med grene i front'..."
                  className="bg-brand-bg text-sm"
                />
                <div className="text-[10px] text-brand-muted mt-1.5">Kun synlig for admin — deles aldrig med kunden.</div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-muted">Historik</div>
                {selected.leads.map((lead, i) => (
                  <div
                    key={lead.id}
                    data-testid={`admin-lead-${i}`}
                    className="rounded-2xl border border-brand-border bg-brand-surface p-5"
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-brand-muted flex items-center gap-2">
                          {lead.type === "quote" ? "Tilbudsanmodning" : "Kontaktbesked"} · {formatDate(lead.created_at)}
                          {lead.source === "manual" && (
                            <span className="px-1.5 py-0.5 rounded bg-brand-soft text-brand-primary text-[10px] uppercase tracking-widest">Manuel</span>
                          )}
                        </div>
                        <div className="font-medium text-brand-text mt-1">
                          {lead.service || "—"}
                        </div>
                        {lead.price != null && (
                          <div className="mt-2">
                            <div className="text-xs text-brand-muted">Grundpris</div>
                            <div className="text-lg font-display text-brand-primary">{formatDKK(lead.price)}</div>
                            {lead.extras_total > 0 && (
                              <div className="mt-1 text-xs text-brand-muted">
                                + Tillæg: <span className="text-brand-text font-medium">{formatDKK(lead.extras_total)}</span>
                                {" = "}
                                <span className="text-brand-primary font-display text-base">{formatDKK(lead.total_price)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Select value={lead.status} onValueChange={(v) => updateStatus(lead, v)}>
                          <SelectTrigger data-testid={`admin-status-${i}`} className="w-36 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(lead)}
                            data-testid={`admin-edit-lead-${i}`}
                            className="text-xs text-brand-primary hover:text-brand-hover inline-flex items-center gap-1"
                          >
                            <Pencil className="h-3 w-3" /> Rediger
                          </button>
                          <button
                            onClick={() => deleteLead(lead)}
                            data-testid={`admin-delete-lead-${i}`}
                            className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                          >
                            <Trash2 className="h-3 w-3" /> Slet
                          </button>
                        </div>
                      </div>
                    </div>

                    {lead.type === "quote" && (
                      <div className="text-xs text-brand-muted grid grid-cols-2 gap-x-4 gap-y-1 mt-2 pt-3 border-t border-brand-border/60">
                        <div><span className="text-brand-text/60">Længde:</span> {lead.length_meters} m</div>
                        <div><span className="text-brand-text/60">Højde:</span> {lead.height_category}</div>
                        <div><span className="text-brand-text/60">Bortkørsel:</span> {lead.include_waste_removal ? "Ja" : "Nej"}</div>
                        <div><span className="text-brand-text/60">Tillæg:</span> {lead.additional_services?.length ? lead.additional_services.join(", ") : "Ingen"}</div>
                        {lead.address && <div className="col-span-2"><span className="text-brand-text/60">Adresse:</span> {lead.address}</div>}
                      </div>
                    )}

                    {lead.message && (
                      <div className="mt-3 text-sm bg-brand-soft/50 rounded-xl p-3 text-brand-text/90 whitespace-pre-wrap">
                        {lead.message}
                      </div>
                    )}

                    {lead.type === "quote" && (
                      <div className="mt-4 pt-3 border-t border-brand-border/60">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs uppercase tracking-widest text-brand-muted">Tillægspriser</div>
                          {extraForm.leadId !== lead.id && (
                            <button
                              onClick={() => setExtraForm({ leadId: lead.id, label: "", amount: "" })}
                              data-testid={`admin-add-extra-btn-${i}`}
                              className="text-xs text-brand-primary inline-flex items-center gap-1 hover:underline"
                            >
                              <Plus className="h-3 w-3" /> Tilføj tillæg
                            </button>
                          )}
                        </div>
                        {lead.extras && lead.extras.length > 0 && (
                          <ul className="space-y-1.5">
                            {lead.extras.map((ex) => (
                              <li key={ex.id} className="flex items-center justify-between text-sm bg-brand-bg rounded-lg px-3 py-2">
                                <span className="text-brand-text">{ex.label}</span>
                                <span className="flex items-center gap-3">
                                  <span className="font-medium text-brand-primary">{formatDKK(ex.amount)}</span>
                                  <button
                                    onClick={() => removeExtra(lead, ex.id)}
                                    data-testid={`admin-remove-extra-${ex.id}`}
                                    className="text-red-400 hover:text-red-600"
                                    title="Fjern tillæg"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {extraForm.leadId === lead.id && (
                          <div className="mt-2 flex gap-2 items-center bg-brand-soft/40 rounded-lg p-2">
                            <Input
                              data-testid={`admin-extra-label-${i}`}
                              placeholder="Beskrivelse (fx Ekstra kørsel)"
                              value={extraForm.label}
                              onChange={(e) => setExtraForm({ ...extraForm, label: e.target.value })}
                              className="flex-1 h-9 bg-brand-surface"
                            />
                            <Input
                              data-testid={`admin-extra-amount-${i}`}
                              placeholder="kr"
                              type="number"
                              value={extraForm.amount}
                              onChange={(e) => setExtraForm({ ...extraForm, amount: e.target.value })}
                              className="w-24 h-9 bg-brand-surface"
                            />
                            <button
                              onClick={() => addExtra(lead)}
                              disabled={addingExtra}
                              data-testid={`admin-save-extra-${i}`}
                              className="inline-flex items-center rounded-full bg-brand-primary text-brand-bg px-3 h-9 text-xs font-medium hover:bg-brand-hover disabled:opacity-60"
                            >
                              {addingExtra ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Gem"}
                            </button>
                            <button
                              onClick={() => setExtraForm({ leadId: null, label: "", amount: "" })}
                              className="text-brand-muted hover:text-brand-text px-2"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        {(!lead.extras || lead.extras.length === 0) && extraForm.leadId !== lead.id && (
                          <div className="text-xs text-brand-muted">Ingen tillæg endnu.</div>
                        )}
                      </div>
                    )}

                    <div className="flex justify-end mt-3 gap-3">
                      {lead.type === "contact" && (
                        <button
                          onClick={() => openQuoteFromContact(selected, lead)}
                          data-testid={`admin-quote-from-contact-${i}`}
                          className="text-xs text-brand-primary inline-flex items-center gap-1 hover:underline font-medium"
                        >
                          <Plus className="h-3 w-3" /> Lav tilbud
                        </button>
                      )}
                      <button
                        onClick={() => openFollowup(selected, lead)}
                        data-testid={`admin-followup-lead-${i}`}
                        className="text-xs text-brand-primary inline-flex items-center gap-1 hover:underline"
                      >
                        <Send className="h-3 w-3" /> Send opfølgning på denne
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Follow-up email dialog */}
      <Dialog open={followupOpen} onOpenChange={setFollowupOpen}>
        <DialogContent className="max-w-lg bg-brand-surface" data-testid="admin-followup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send follow-up mail</DialogTitle>
            <DialogDescription>
              Til: <strong>{followupTo?.customer?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendFollowup} className="space-y-4">
            <div>
              <Label className="text-xs">Emne</Label>
              <Input
                data-testid="followup-subject"
                value={followupForm.subject}
                onChange={(e) => setFollowupForm({ ...followupForm, subject: e.target.value })}
                required
                className="mt-1.5 bg-brand-bg"
              />
            </div>
            <div>
              <Label className="text-xs">Besked</Label>
              <Textarea
                data-testid="followup-message"
                rows={8}
                value={followupForm.message}
                onChange={(e) => setFollowupForm({ ...followupForm, message: e.target.value })}
                required
                className="mt-1.5 bg-brand-bg"
              />
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={sending}
                data-testid="followup-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary text-brand-bg px-6 py-3 font-medium hover:bg-brand-hover disabled:opacity-60"
              >
                {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sender...</> : <><Send className="h-4 w-4" /> Send mail</>}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Edit lead dialog */}
      <Dialog open={!!editLead} onOpenChange={(o) => !o && setEditLead(null)}>
        <DialogContent className="max-w-lg bg-brand-surface max-h-[90vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Rediger {editLead?.type === "quote" ? "tilbud" : "besked"}
            </DialogTitle>
            <DialogDescription>
              Ret alle felter — inklusive kundeoplysninger og pris.
            </DialogDescription>
          </DialogHeader>
          {editLead && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Navn</Label>
                  <Input
                    data-testid="edit-name"
                    value={editForm.name || ""}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="mt-1.5 bg-brand-bg"
                  />
                </div>
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input
                    data-testid="edit-phone"
                    value={editForm.phone || ""}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="mt-1.5 bg-brand-bg"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">E-mail</Label>
                <Input
                  data-testid="edit-email"
                  type="email"
                  value={editForm.email || ""}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1.5 bg-brand-bg"
                />
              </div>

              {editLead.type === "quote" ? (
                <>
                  <div>
                    <Label className="text-xs">Adresse</Label>
                    <Input
                      data-testid="edit-address"
                      value={editForm.address || ""}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="mt-1.5 bg-brand-bg"
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Længde (m)</Label>
                      <Input
                        data-testid="edit-length"
                        type="number" min={1}
                        value={editForm.length_meters ?? ""}
                        onChange={(e) => setEditForm({ ...editForm, length_meters: e.target.value })}
                        className="mt-1.5 bg-brand-bg"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Højde</Label>
                      <Select value={editForm.height_category} onValueChange={(v) => setEditForm({ ...editForm, height_category: v })}>
                        <SelectTrigger data-testid="edit-height" className="mt-1.5 h-10 bg-brand-bg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1,5 – 2,5 meter">1,5 – 2,5 m</SelectItem>
                          <SelectItem value="Over 2,5 meter">Over 2,5 m</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Pris (kr)</Label>
                      <Input
                        data-testid="edit-price"
                        type="number" min={0}
                        value={editForm.estimated_price ?? ""}
                        onChange={(e) => setEditForm({ ...editForm, estimated_price: e.target.value })}
                        className="mt-1.5 bg-brand-bg"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="edit-waste"
                      checked={!!editForm.include_waste_removal}
                      onChange={(e) => setEditForm({ ...editForm, include_waste_removal: e.target.checked })}
                      className="h-4 w-4"
                    />
                    Bortkørsel inkluderet
                  </label>
                </>
              ) : (
                <div>
                  <Label className="text-xs">Ydelse</Label>
                  <Input
                    data-testid="edit-service"
                    value={editForm.service || ""}
                    onChange={(e) => setEditForm({ ...editForm, service: e.target.value })}
                    className="mt-1.5 bg-brand-bg"
                  />
                </div>
              )}

              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-status" className="mt-1.5 h-10 bg-brand-bg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">
                  {editLead.type === "quote" ? "Bemærkninger" : "Besked"}
                </Label>
                <Textarea
                  data-testid="edit-notes"
                  rows={3}
                  value={(editLead.type === "quote" ? editForm.notes : editForm.message) || ""}
                  onChange={(e) => setEditForm(editLead.type === "quote"
                    ? { ...editForm, notes: e.target.value }
                    : { ...editForm, message: e.target.value })}
                  className="mt-1.5 bg-brand-bg"
                />
              </div>

              <DialogFooter>
                <button
                  type="submit"
                  disabled={savingEdit}
                  data-testid="edit-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary text-brand-bg px-6 py-3 font-medium hover:bg-brand-hover disabled:opacity-60"
                >
                  {savingEdit ? <><Loader2 className="h-4 w-4 animate-spin" /> Gemmer...</> : <><Save className="h-4 w-4" /> Gem ændringer</>}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add customer dialog */}
      <Dialog open={addCustOpen} onOpenChange={setAddCustOpen}>
        <DialogContent className="max-w-lg bg-brand-surface max-h-[90vh] overflow-y-auto" data-testid="admin-add-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Tilføj kunde manuelt</DialogTitle>
            <DialogDescription>
              Opret et tilbud direkte i systemet — fx efter et telefonopkald eller besigtigelse.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createCustomer} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Navn *</Label>
                <Input
                  data-testid="add-cust-name"
                  value={addCustForm.name}
                  onChange={(e) => setAddCustForm({ ...addCustForm, name: e.target.value })}
                  required className="mt-1.5 bg-brand-bg"
                />
              </div>
              <div>
                <Label className="text-xs">Telefon *</Label>
                <Input
                  data-testid="add-cust-phone"
                  value={addCustForm.phone}
                  onChange={(e) => setAddCustForm({ ...addCustForm, phone: e.target.value })}
                  required className="mt-1.5 bg-brand-bg"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">E-mail *</Label>
              <Input
                data-testid="add-cust-email"
                type="email"
                value={addCustForm.email}
                onChange={(e) => setAddCustForm({ ...addCustForm, email: e.target.value })}
                required className="mt-1.5 bg-brand-bg"
              />
            </div>
            <div>
              <Label className="text-xs">Adresse</Label>
              <Input
                data-testid="add-cust-address"
                value={addCustForm.address}
                onChange={(e) => setAddCustForm({ ...addCustForm, address: e.target.value })}
                className="mt-1.5 bg-brand-bg"
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Længde (m) *</Label>
                <Input
                  data-testid="add-cust-length"
                  type="number" min={1}
                  value={addCustForm.length_meters}
                  onChange={(e) => setAddCustForm({ ...addCustForm, length_meters: e.target.value })}
                  required className="mt-1.5 bg-brand-bg"
                />
              </div>
              <div>
                <Label className="text-xs">Højde</Label>
                <Select value={addCustForm.height_category} onValueChange={(v) => setAddCustForm({ ...addCustForm, height_category: v })}>
                  <SelectTrigger data-testid="add-cust-height" className="mt-1.5 h-10 bg-brand-bg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1,5 – 2,5 meter">1,5 – 2,5 m</SelectItem>
                    <SelectItem value="Over 2,5 meter">Over 2,5 m</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pris (kr) *</Label>
                <Input
                  data-testid="add-cust-price"
                  type="number" min={0}
                  value={addCustForm.estimated_price}
                  onChange={(e) => setAddCustForm({ ...addCustForm, estimated_price: e.target.value })}
                  required className="mt-1.5 bg-brand-bg"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={addCustForm.status} onValueChange={(v) => setAddCustForm({ ...addCustForm, status: v })}>
                <SelectTrigger data-testid="add-cust-status" className="mt-1.5 h-10 bg-brand-bg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Bemærkninger</Label>
              <Textarea
                data-testid="add-cust-notes"
                rows={3}
                value={addCustForm.notes}
                onChange={(e) => setAddCustForm({ ...addCustForm, notes: e.target.value })}
                className="mt-1.5 bg-brand-bg"
              />
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={addingCust}
                data-testid="add-cust-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary text-brand-bg px-6 py-3 font-medium hover:bg-brand-hover disabled:opacity-60"
              >
                {addingCust ? <><Loader2 className="h-4 w-4 animate-spin" /> Opretter...</> : <><UserPlus className="h-4 w-4" /> Opret kunde</>}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, "data-testid": testId }) {
  return (
    <div data-testid={testId} className="bg-brand-surface rounded-3xl border border-brand-border p-6">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-muted">{label}</div>
        <Icon className="h-4 w-4 text-brand-primary" />
      </div>
      <div className="font-display text-3xl mt-3 text-brand-text">{value}</div>
      {sub && <div className="text-xs text-brand-muted mt-1">{sub}</div>}
    </div>
  );
}


function MonthlyChart({ labels, values }) {
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-3 h-40" data-testid="monthly-chart">
      {values.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-2">
          <div className="flex-1 w-full flex items-end">
            <div
              className="w-full bg-brand-primary rounded-t-lg transition-all"
              style={{ height: `${(v / max) * 100}%`, minHeight: v ? "8px" : "2px" }}
              title={`${v}`}
            />
          </div>
          <div className="text-[10px] text-brand-muted">{labels[i]?.slice(5)}</div>
          <div className="text-xs font-medium">{v}</div>
        </div>
      ))}
    </div>
  );
}
